import { $, html, Html, View } from 'rune-ts';
import { ProductStoryImage } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_ProductContent/types.ts';
import klass from './ImageUploader.module.scss';
import { ImageProcessors } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_ProductContent/helpers/image-processors.ts';
import { shareFont } from '@services/MarppleShop/shared/typography';
import { ArrowDownIcon, ArrowUpicon } from '@services/MarppleShop/renderApp/components/atoms/Icon';
import { TrashBinIcon } from '@services/MarppleShop/shared/components/atoms/Icon/foundation_icons.ts';
import { Alert } from '@services/MarppleShop/renderApp/components/cells/Alert/Alert.ts';
import { FileUploadError } from '@services/MarppleShop/shared/components/cells/FileUpload/FileUploadHelper.ts';
import { ButtonView } from '@services/MarppleShop/shared/components/atoms/MarppleShopDesignSystem/Buttons/ButtonView.ts';
import {
  PRODUCT_STORY_MAX_WIDTH,
  PRODUCT_STORY_MAX_HEIGHT,
} from '@modules/Creator/ProductStoryEditor/S/width';
import { htmlIfElse } from '@services/MarppleShop/shared/util';

interface ImageUploaderProps {
  images?: ProductStoryImage[];
  onChange?: (images: ProductStoryImage[]) => void;
  is_mobile?: boolean;
}

export class ImageUploader extends View<ImageUploaderProps> {
  private images: ProductStoryImage[] = this.data.images || [];

  protected override template(): Html {
    const is_mobile = Boolean($('html[is_mobile="true"]'));

    return html`
      <div class="${klass.root}">
        <div class="${klass.upload_box}">
          <div class="${klass.text_wrap}">
            <span class="${shareFont('14_bold')}">상세 이미지를 등록해 주세요.</span>
            ${htmlIfElse(
              is_mobile,
              html`
                <div class="${klass.content} ${shareFont('12_medium')}">
                  <div class="${klass.content_wrap}">
                    이미지 최대 사이즈 : ${PRODUCT_STORY_MAX_WIDTH}px * ${PRODUCT_STORY_MAX_HEIGHT}px
                    <span class="${klass.divide}"></span>
                  </div>
                  <div class="${klass.content_wrap}">
                    등록 가능 형식 : JPG, PNG, GIF
                    <span class="${klass.divide}"></span>
                    용량 5MB 이하
                  </div>
                </div>
              `,
              html`
                <span class="${klass.content} ${shareFont('12_medium')}">
                  이미지 최대 사이즈 : ${PRODUCT_STORY_MAX_WIDTH}px * ${PRODUCT_STORY_MAX_HEIGHT}px
                  <span class="${klass.divide}"></span>
                  등록 가능 형식 : JPG, PNG, GIF
                  <span class="${klass.divide}"></span>
                  용량 5MB 이하
                </span>
              `,
            )}
          </div>
          ${new ButtonView({
            size: 32,
            color: 'Primary_A',
            label: '이미지 등록하기',
            onClick: () => this.handleImageUpload(),
          })}
        </div>
        <div class="${klass.images_wrap}">
          ${this.images.map((image, index) => this.makeImageItemHtml(image, index))}
        </div>
      </div>
    `;
  }

  protected override onRender() {
    this.delegate('click', `.${klass.button}`, (e: Event) => {
      const target = e.currentTarget;
      if (!(target instanceof HTMLElement)) return;

      const action = target.dataset.action;
      const current_index = target.closest<HTMLElement>('[data-index]')?.dataset.index;

      if (!action || !current_index) return;

      switch (action) {
        case 'up': {
          this.moveImage(Number(current_index), 'up');
          break;
        }
        case 'down': {
          this.moveImage(Number(current_index), 'down');
          break;
        }
        case 'remove': {
          this.removeImage(Number(current_index));
          break;
        }
      }
    });
  }

  private makeImageItemHtml(image: ProductStoryImage, index: number) {
    return html`
      <div
        class="${klass.image_item}"
        data-id="${image.id}"
        data-file-id="${image.file_id}"
        data-index="${index}"
      >
        <img src="${image.url}" alt="상품 이미지" />
        <div class="${klass.button_box}">
          <div data-action="up" class="${klass.button}">
            <div class="${klass.icon_wrap}">${ArrowUpicon()}</div>
            <span class="${shareFont('12_medium')}">위로 이동</span>
          </div>
          <div data-action="down" class="${klass.button}">
            <div class="${klass.icon_wrap}">${ArrowDownIcon()}</div>
            <span class="${shareFont('12_medium')}">아래로 이동</span>
          </div>
          <div data-action="remove" class="${klass.button}">
            <div class="${klass.icon_wrap}">${TrashBinIcon()}</div>
            <span class="${shareFont('12_medium')}">삭제</span>
          </div>
        </div>
      </div>
    `;
  }

  private moveImage(imageIndex: number, direction: 'up' | 'down'): void {
    const newIndex = direction === 'up' ? imageIndex - 1 : imageIndex + 1;

    if (newIndex < 0 || newIndex >= this.images.length) return;

    const temp = this.images[imageIndex];
    const newItem = this.images[newIndex];

    if (temp && newItem) {
      this.images[imageIndex] = newItem;
      this.images[newIndex] = temp;
    }

    // no 속성 재정렬
    this.images = this.images.map((img, index) => ({
      ...img,
      no: index,
    }));

    this.updateProductStoryImages();
    this.redraw();
  }

  private removeImage(imageIndex: number): void {
    this.images = this.images.filter((_, index) => index !== imageIndex);

    // no 속성 재정렬
    this.images = this.images.map((img, index) => ({
      ...img,
      no: index,
    }));

    this.updateProductStoryImages();
    this.redraw();
  }

  private async handleImageUpload() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/gif';
    input.multiple = true;

    input.onchange = async () => {
      const files = input.files;
      if (!files || files.length === 0) return;

      try {
        const result = await ImageProcessors.processMultipleImageFiles(files, this.images.length);

        if (result.error) {
          return Alert.open({ title: result.error.title, message: result.error.message });
        }

        if (result.processedImages) {
          const newImages: ProductStoryImage[] = result.processedImages.map((img, index) => ({
            id: img.id,
            url: img.url,
            no: this.images.length + index,
            is_hidden: false,
            created_at: new Date().toISOString(),
            updated_at: null,
          }));

          this.images = [...this.images, ...newImages];
          this.updateProductStoryImages();
          this.redraw();
        }
      } catch (error) {
        if (error instanceof FileUploadError) {
          return Alert.open(error.json);
        }

        return Alert.open({
          title: '파일 첨부 실패',
          message: '예상치 못한 에러가 발생했습니다.\n업로드를 다시 시도해주세요.',
        });
      }
    };

    input.click();
  }

  private updateProductStoryImages(): void {
    this.data.onChange?.(this.images);
  }
}
